import React, { Fragment } from "react";
import { styled } from "@mui/material/styles";
import {
  KeyValue,
  Query,
  SearchPillData,
  TableHeader,
} from "../../../../interfaces";
import {
  fileNameFormat,
  reduceObj,
} from "../../../../common/services/Utilities";
import SearchPills from "../../../../containers/SearchPills";
import {
  Box,
  Button,
  Chip,
  Divider,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { fetchData } from "../../../../common/services/ApiClient";
import { ApiBaseUrl } from "../../../../config";
import MultiHeaderTable from "../../../MultiHeaderTable/MultiHeaderTable";
import {
  additionalHeaderColor,
  primaryHeaderColor,
  secondaryHeaderColor,
  textHeaderColor,
} from "../../../../refdata/constants";
import LoadingIndicator from "../../../LoadingIndicator";
import { selectedStatesAlpha } from "../../../../refdata/FilterOptions";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DepositsNameSearchTable from "../../../../containers/DepositsNameSearchTable";
import ApiDataDownload, { apiDownloadProps } from "../../../ApiDataDownload";
import GrowthRateMSATable from "./GrowthRateMSATable";
import clsx from "clsx";
import ModalDialog from "../../../ModalDialog";
import BranchOfficeDeposits from "../../../../containers/BranchOfficeDeposits";
import ShowHideButton from "../../../ShowHideButton";

const PREFIX = "GrowthRate";

const classes = {
  stateRow: `${PREFIX}-stateRow`,
  group: `${PREFIX}-group`,
  stateRow2: `${PREFIX}-stateRow2`,
  footer: `${PREFIX}-footer`,
  footerText: `${PREFIX}-footerText`,
  rowIndent: `${PREFIX}-rowIndent`,
  countyRow: `${PREFIX}-countyRow`,
  subHeading: `${PREFIX}-subHeading`,
  sum: `${PREFIX}-sum`,
  chip: `${PREFIX}-chip`,
  openCloseAll: `${PREFIX}-openCloseAll`,
  groupHover: `${PREFIX}-groupHover`,
  groupHoverState: `${PREFIX}-groupHoverState`,
  groupHoverCounty: `${PREFIX}-groupHoverCounty`,
  moreInfo: `${PREFIX}-moreInfo`,
};

const Root = styled("div")(({ theme }) => ({
  "& #multi-header-table-head": {
    "position": "sticky",
    "top": 0,
    "& tr": {
      position: "static",
      borderCollaps: "collapse",
    },
    "& th": {
      position: "static",
      borderCollaps: "collapse",
    },
  },
  [`& .${classes.stateRow}`]: {
    "cursor": "pointer",
    "& td": {
      backgroundColor: "#acc7df",
      fontWeight: "bold",
    },
    "&:hover td, &:focus-visible td": {
      backgroundColor: "#CED6E3",
    },
  },

  [`& .${classes.group}`]: {},

  [`& .${classes.stateRow2}`]: {
    "cursor": "default",
    "& td": {
      backgroundColor: "#acc7df",
      fontWeight: "bold",
    },
  },

  [`& .${classes.footer}`]: {
    color: theme.palette.primary.main,
    lineHeight: "24px",
    fontSize: "16px",
    fontWeight: 400,
    textAlign: "left",
  },

  [`& .${classes.footerText}`]: {
    color: theme.palette.primary.main,
    width: 700,
    minWidth: 700,
    fontSize: 16,
    whiteSpace: "nowrap",
  },

  [`& .${classes.rowIndent}`]: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      textAlign: "left",
    },
    "fontWeight": "bold",
    "textIndent": "20px",
    "textWrap": "nowrap",
    "textAlign": "left",
  },

  [`& .${classes.countyRow}`]: {
    "& td:first-child": {
      textIndent: 54,
    },
  },

  [`& .${classes.subHeading}`]: {
    color: theme.palette.primary.main,
    width: 700,
    minWidth: 700,
    fontSize: 20,
    fontWeight: "bold",
    whiteSpace: "nowrap",
    paddingBottom: "1rem",
  },

  [`& .${classes.sum}`]: {
    fontWeight: "bold",
    textAlign: "right",
  },

  [`& .${classes.chip}`]: {
    "cursor": "pointer",
    "backgroundColor": "transparent",
    "color": theme.palette.primary.main,
    "fontSize": 14,
    "float": "left",
    "& .MuiChip-icon": {
      color: theme.palette.primary.main,
    },
  },

  [`& .${classes.openCloseAll}`]: {
    "minWidth": 80,
    "margin": 0,
    "color": "#ffffff",
    "fontWeight": 600,
    "backgroundColor": "#426cac",
    "&:hover, &.Mui-focusVisible": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.secondary.main,
    },
  },

  [`& .${classes.groupHover}`]: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      backgroundColor: theme.palette.secondary.main,
      cursor: "pointer",
    },
  },

  [`& .${classes.groupHoverCounty}`]: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      backgroundColor: "rgba(0, 0, 0, 0.08)",
      textDecoration: "underline",
      cursor: "pointer",
    },
  },

  [`& .${classes.moreInfo}`]: {
    "color": "#0000ff",
    "fontWeight": "bold",
    "fontSize": 16,
    "lineHeight": "24px",
    "paddingBottom": "10px",
    "fontStyle": "normal",
    "&:hover, &.Mui-focusVisible": {
      textDecoration: "underline",
      cursor: "pointer",
    },
  },
}));

interface Props {
  appState: Query;
  updateFilter: (field: string, value: string | number) => void;
  updateSearch: (field: string, value: string) => void;
}

export default function GrowthRate({
  appState,
  updateFilter,
  updateSearch,
}: Props) {
  const [bankName, setBankName] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [totals, setTotals] = React.useState<KeyValue>({});
  const [noResults, setNoResults] = React.useState(false);
  const startDate = appState.filters.startDate;
  const endDate = appState.filters.endDate;

  const appStateFilters = React.useMemo(() => {
    return {
      startDate: startDate,
      endDate: endDate,
      cert: appState.filters.cert,
      bhcRSSD: appState.filters.bhcRSSD,
      stalpbr: appState.filters.stalpbr,
      name: appState.search.name,
      bhcName: appState.search.bhcName,
    };
  }, [
    appState.filters.bhcRSSD,
    appState.filters.cert,
    appState.filters.stalpbr,
    appState.search.bhcName,
    appState.search.name,
    endDate,
    startDate,
  ]);

  const [previousState, setPreviousState] = React.useState(appStateFilters);
  const [groupHover, setGroupHover] = React.useState("");

  const [prevInstType, setPrevInstType] = React.useState(
    appState.ui.institutionType,
  );
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [growthRateTableData, setGrowthRateTableData] =
    React.useState<KeyValue>({});

  const [open, setOpen] = React.useState({ stalp: "", name: "", year: "" });
  const branchOfficeModal = (
    <ModalDialog
      title={bankName}
      display={open.stalp !== ""}
      setDisplay={() => setOpen({ stalp: "", name: "", year: "" })}
      maxWidth="lg"
    >
      <BranchOfficeDeposits
        providedAppState={{
          ...appState,
          filters: {
            ...appState.filters,
            stalpbr: open.stalp,
            lastYear: open.year,
            county:
              selectedStatesAlpha
                .find((state: KeyValue) => state.value === open.stalp)
                ?.label?.toLowerCase() !== open.name?.toLowerCase()
                ? open.name
                : "",
          },
        }}
      />
    </ModalDialog>
  );
  const states = selectedStatesAlpha.map((obj) => obj.label);
  const tableCellProps = (
    stalp: string,
    name: string,
    year: string,
    value?: string,
  ) => {
    const clickThroughFn = (e: any) => {
      if ((e.key && e.key !== "Enter") || value === "0") {
        return;
      }
      if (
        (appState.filters.bhcRSSD || appState.filters.cert) &&
        appState.filters.startDate &&
        appState.filters.endDate
      ) {
        setOpen({ stalp, name, year });
      }
    };
    return {
      onMouseEnter: () => value !== "0" && setGroupHover(`${name}_${year}`),
      onMouseLeave: () => setGroupHover(""),
      onFocus: () => value !== "0" && setGroupHover(`${name}_${year}`),
      onBlur: () => setGroupHover(""),
      onClick: clickThroughFn,
      onKeyDown: clickThroughFn,
      tabIndex: 0,
      className: clsx(
        groupHover === `${name}_${year}`
          ? name?.toLowerCase() ===
            selectedStatesAlpha
              .find((state: KeyValue) => state.value === open.stalp)
              ?.label?.toLowerCase()
            ? classes.groupHover
            : states.find((st) => st === name)
              ? classes.groupHoverState
              : classes.groupHoverCounty
          : classes.group,
      ),
    };
  };
  const handleScrollToBottom = () => {
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: "smooth",
    });
  };

  const growthRateSearchPills: SearchPillData[] = [
    {
      label: "CERT",
      value: appState.filters.cert ? appState.filters.cert.toString() : "",
      static: true,
      formKey: "cert",
    },
    {
      label: "RSSDHCR",
      value: appState.filters.bhcRSSD ? appState.filters.bhcRSSD : "",
      search: true,
      formKey: "bhcRSSD",
      static: true,
    },
    {
      label: "Name",
      value: appState.search.name ? appState.search.name.toString() : "",
      search: true,
      formKey: "name",
      static: true,
    },
    {
      label: "BHC Name",
      value: appState.search.bhcName ? appState.search.bhcName.toString() : "",
      search: true,
      formKey: "bhcName",
      static: true,
    },
    {
      label: "State",
      value: appState.filters.stalpbr === "ALL" ? "" : appState.filters.stalpbr,
      static: false,
      formKey: "stalpbr",
    },
    {
      label: "Starting Period",
      value: `06/30/${startDate}`,
      static: true,
      formKey: "startDate",
    },
    {
      label: "Ending Period",
      value: `06/30/${endDate}`,
      static: true,
      formKey: "endDate",
    },
    {
      label: "",
      value: appState.ui.displayResults === "msas" ? "MSAs" : "",
      ui: true,
      static: true,
      formKey: "displayResults",
    },
  ];

  const searchPills = (
    <ShowHideButton
      title="Selected Search Criteria"
      body={<SearchPills dataArray={growthRateSearchPills} />}
    />
  );

  const headerStyle = (
    bgColor: string,
    align: string,
    textIndent?: string,
    width?: number,
  ): KeyValue => {
    return {
      textAlign: align,
      backgroundColor: bgColor,
      color: textHeaderColor,
      textIndent: textIndent,
      fontWeight: "bold",
      width: width,
    };
  };

  const tableHeaders: TableHeader[][] = [
    [
      {
        title: " ",
        styles: headerStyle(primaryHeaderColor, ""),
        colSpan: 3,
      },
      {
        title: `${startDate}`,
        styles: headerStyle(secondaryHeaderColor, ""),
        colSpan: 2,
      },
      {
        title: `${endDate}`,
        styles: headerStyle(additionalHeaderColor, ""),
        colSpan: 2,
      },
      {
        title: "Change",
        styles: headerStyle(secondaryHeaderColor, ""),
        colSpan: 2,
      },
      {
        title: " ",
        styles: headerStyle(primaryHeaderColor, ""),
        colSpan: 2,
      },
    ],
    (() => {
      const headerValues = [
        "Office Count",
        appState.ui.institutionType === "bhcs"
          ? "Total Combined Deposits* (Thousands USD)"
          : "Total Deposits (Thousands USD)",
      ];

      const openStates = Object.keys(growthRateTableData).map(
        (state: string) => growthRateTableData[state].expanded,
      );

      const openCloseButton =
        Object.keys(growthRateTableData).length > 1 ? (
          <Button
            className={classes.openCloseAll}
            onClick={() => {
              openStates.every((val) => val)
                ? setGrowthRateTableData(
                    reduceObj(
                      Object.keys(growthRateTableData).map((state: string) => {
                        return {
                          key: state,
                          value: {
                            ...growthRateTableData[state],
                            expanded: false,
                          },
                        };
                      }),
                    ),
                  )
                : setGrowthRateTableData(
                    reduceObj(
                      Object.keys(growthRateTableData).map((state: string) => {
                        return {
                          key: state,
                          value: {
                            ...growthRateTableData[state],
                            expanded: true,
                          },
                        };
                      }),
                    ),
                  );
            }}
          >
            {openStates.every((val) => val) ? "Close All" : "Open All"}
          </Button>
        ) : (
          ""
        );
      const stateCountyHeader: TableHeader[] = [
        {
          title: "State / County",
          styles: headerStyle(primaryHeaderColor, "left", "5px", 190),
          colSpan: 1,
          apiField: "",
        },
        {
          title:
            appState.filters.stalpbr && appState.filters.stalpbr !== "ALL"
              ? ""
              : openCloseButton,
          styles: headerStyle(primaryHeaderColor, ""),
          colSpan: 1,
          apiField: "",
        },
        {
          title: "FIPS Code",
          styles: headerStyle(primaryHeaderColor, "", undefined, 50),
          colSpan: 1,
          apiField: "",
        },
      ];
      const startDateHeader: TableHeader[] = headerValues.map(
        (header: string) => {
          return {
            title: header,
            styles: headerStyle(
              secondaryHeaderColor,
              header === "Office Count" ? "" : "right",
              undefined,
              header === "Office Count" ? 40 : 170,
            ),
            colSpan: 1,
            apiField: "",
          };
        },
      );

      const endDateHeader: TableHeader[] = headerValues.map(
        (header: string) => {
          return {
            title: header,
            styles: headerStyle(
              additionalHeaderColor,
              header === "Office Count" ? "" : "right",
              undefined,
              header === "Office Count" ? 40 : 170,
            ),
            colSpan: 1,
            apiField: "",
          };
        },
      );

      const changeHeader: TableHeader[] = headerValues.map((header: string) => {
        return {
          title: header,
          styles: headerStyle(
            secondaryHeaderColor,
            header === "Office Count" ? "" : "right",
            undefined,
            header === "Office Count" ? 40 : 170,
          ),
          colSpan: 1,
          apiField: "",
        };
      });

      const growthRateHeader: TableHeader[] = [
        {
          title: "Deposit Growth Rate (%)",
          styles: headerStyle(primaryHeaderColor, "", undefined, 140),
          colSpan: 1,

          apiField: "",
        },
      ];

      return [
        ...stateCountyHeader,
        ...startDateHeader,
        ...endDateHeader,
        ...changeHeader,
        ...growthRateHeader,
      ];
    })(),
  ];

  const moreInformation = appState.ui.institutionType === "bhcs" && (
    <Typography className={classes.moreInfo} onClick={handleScrollToBottom}>
      Click for important information about this data.
    </Typography>
  );

  React.useEffect(() => {
    if (
      appState.ui.institutionType === "banks" &&
      prevInstType !== appState.ui.institutionType
    ) {
      setPrevInstType(appState.ui.institutionType);
      updateFilter("bhcRSSD", "");
    }
    if (
      appState.ui.institutionType === "bhcs" &&
      prevInstType !== appState.ui.institutionType
    ) {
      setPrevInstType(appState.ui.institutionType);
      updateSearch("name", "");
      updateFilter("cert", "");
    }
  }, [appState.ui.institutionType, prevInstType, updateFilter, updateSearch]);

  React.useEffect(() => {
    if (
      prevInstType === appState.ui.institutionType &&
      (appState.filters.cert || appState.filters.bhcRSSD) &&
      startDate &&
      endDate &&
      (appStateFilters !== previousState || initialLoad)
    ) {
      let isMounted = true;
      initialLoad && setInitialLoad(false);
      setLoading(true);

      setPreviousState(appStateFilters);
      const stateFilter =
        appState.filters.stalpbr && appState.filters.stalpbr !== "ALL"
          ? ` AND STALPBR:"${appState.filters.stalpbr}"`
          : "";
      const nameOrNameHCR = appState.filters.cert ? "NAMEFULL" : "NAMEHCR";
      const certRSSDFilter = appState.filters.cert
        ? `CERT:${appState.filters.cert}`
        : `RSSDHCR:${appState.filters.bhcRSSD}`;
      (async () => {
        await Promise.all([
          fetchData(
            `${ApiBaseUrl()}/sod?filters=${certRSSDFilter} AND YEAR:(${startDate} OR ${endDate})&limit=1&sort_by=YEAR&sort_order=DESC&fields=${nameOrNameHCR}`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=STALPBR&get_unique=CERT&&agg_sum_fields=DEPSUMBR&filters=YEAR:${startDate} AND ${certRSSDFilter}${stateFilter}`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=STALPBR&get_unique=CERT&agg_sum_fields=DEPSUMBR&filters=YEAR:${endDate} AND ${certRSSDFilter}${stateFilter}`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=STALPBR&agg_sum_fields=DEPSUMBR&filters=YEAR:${startDate} AND ${certRSSDFilter}${stateFilter}`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=STALPBR&agg_sum_fields=DEPSUMBR&filters=YEAR:${endDate} AND ${certRSSDFilter}${stateFilter}`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?filters=YEAR:${startDate} AND ${certRSSDFilter}${stateFilter}&agg_by=STCNTYBR&agg_sum_fields=DEPSUMBR&agg_limit=1500&sort_by=STCNTYBR&sort_order=ASC`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?filters=YEAR:${endDate} AND ${certRSSDFilter}${stateFilter}&agg_by=STCNTYBR&agg_sum_fields=DEPSUMBR&agg_limit=1500&sort_by=STCNTYBR&sort_order=ASC`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?filters=YEAR:(${startDate} OR ${endDate}) AND ${certRSSDFilter}${stateFilter}&fields=STCNTYBR,STALPBR,CNTYNAMB&limit=10000&get_unique=STCNTYBR`,
          ),
        ])
          .then((response) => {
            setNoResults(
              response[3]?.totals?.count <= 0 &&
                response[4]?.totals?.count <= 0,
            );
            if (isMounted) {
              setBankName(
                response[0].data[0] ? response[0].data[0][nameOrNameHCR] : "",
              );
              const normalRate =
                (response[2]?.totals?.sum_DEPSUMBR
                  ? response[2]?.totals?.sum_DEPSUMBR -
                    response[1]?.totals?.sum_DEPSUMBR
                  : 0 - response[1]?.totals?.sum_DEPSUMBR) /
                response[1]?.totals?.sum_DEPSUMBR;

              const altRate = !response[1]?.totals?.sum_DEPSUMBR
                ? 1
                : normalRate;
              setTotals({
                startDate: {
                  count: response[3]?.totals?.count ?? 0,
                  sum_DEPSUMBR: response[1]?.totals?.sum_DEPSUMBR ?? 0,
                },
                endDate: {
                  count: response[4]?.totals?.count ?? 0,
                  sum_DEPSUMBR: response[2]?.totals?.sum_DEPSUMBR || 0,
                },
                change: {
                  count:
                    response[4]?.totals?.count - response[3]?.totals?.count,
                  sum_DEPSUMBR:
                    (response[2]?.totals?.sum_DEPSUMBR ?? 0) -
                    (response[1]?.totals?.sum_DEPSUMBR ?? 0),
                },
                rate:
                  response[1]?.totals?.sum_DEPSUMBR +
                    response[2]?.totals?.sum_DEPSUMBR ===
                  0
                    ? 0
                    : altRate,
              });
              const countyDataStart = response[5].data;
              const countyDataEnd = response[6].data;
              const tableData: KeyValue = {};

              response[1].data.forEach((dataElem: KeyValue) => {
                const key = dataElem.STALPBR;
                if (!tableData[key]) {
                  tableData[key] = {};
                }
                tableData[key][startDate] = {
                  count: dataElem.count,
                  sum_DEPSUMBR: dataElem.sum_DEPSUMBR,
                };
              });

              let endDateRes = response[2].data;
              if (!endDateRes.length) {
                endDateRes = response[1].data.map((data: KeyValue) => {
                  return { ...data, count: 0, sum_DEPSUMBR: 0 };
                });
              }

              endDateRes.forEach((dataElem: KeyValue) => {
                const key = dataElem.STALPBR;
                const allCounties: string[] = [];
                const combinedCountyDataStart = countyDataStart.map(
                  (data: KeyValue) => {
                    const matchingCounty = response[7].data.find(
                      (structure: KeyValue) =>
                        data.STCNTYBR === structure.STCNTYBR,
                    );
                    return matchingCounty
                      ? {
                          ...data,
                          STALPBR: matchingCounty?.STALPBR,
                          CNTYNAMB: matchingCounty?.CNTYNAMB,
                        }
                      : data;
                  },
                );
                const combinedCountyDataEnd = countyDataEnd.map(
                  (data: KeyValue) => {
                    const matchingCounty = response[7].data.find(
                      (structure: KeyValue) =>
                        data.STCNTYBR === structure.STCNTYBR,
                    );
                    return matchingCounty
                      ? {
                          ...data,
                          STALPBR: matchingCounty?.STALPBR,
                          CNTYNAMB: matchingCounty?.CNTYNAMB,
                        }
                      : data;
                  },
                );
                const matchingCountyDataStart = combinedCountyDataStart
                  .filter(
                    (county: KeyValue) =>
                      county.STALPBR && county.STALPBR === key,
                  )
                  .map((county: KeyValue) => {
                    !allCounties.includes(county.CNTYNAMB) &&
                      allCounties.push(county.CNTYNAMB);
                    return county;
                  });

                const matchingCountyDataEnd = combinedCountyDataEnd
                  .filter(
                    (county: KeyValue) =>
                      county.STALPBR && county.STALPBR === key,
                  )
                  .map((county: KeyValue) => {
                    !allCounties.includes(county.CNTYNAMB) &&
                      allCounties.push(county.CNTYNAMB);
                    return county;
                  });

                if (!tableData[key]) {
                  tableData[key] = {};
                }

                tableData[key][endDate] = {
                  count: dataElem.count,
                  sum_DEPSUMBR: dataElem.sum_DEPSUMBR,
                };
                if (!tableData[key][startDate]) {
                  tableData[key][startDate] = {
                    count: 0,
                    sum_DEPSUMBR: 0,
                  };
                }
                const normalRate =
                  (tableData[key][endDate].sum_DEPSUMBR -
                    tableData[key][startDate].sum_DEPSUMBR) /
                  tableData[key][startDate].sum_DEPSUMBR;
                const altRate =
                  tableData[key][startDate].sum_DEPSUMBR === 0 ? 1 : normalRate;

                tableData[key]["change"] = {
                  count:
                    tableData[key][endDate].count -
                    tableData[key][startDate].count,
                  sum_DEPSUMBR:
                    tableData[key][endDate].sum_DEPSUMBR -
                    tableData[key][startDate].sum_DEPSUMBR,
                  rate:
                    tableData[key][startDate].sum_DEPSUMBR +
                      tableData[key][endDate].sum_DEPSUMBR ===
                    0
                      ? 0
                      : altRate,
                };
                tableData[key]["expanded"] = false;
                tableData[key]["counties"] = allCounties.map(
                  (county: string) => {
                    const start = matchingCountyDataStart.find(
                      (endCounty: KeyValue) => endCounty.CNTYNAMB === county,
                    ) || { count: 0, sum_DEPSUMBR: 0 };
                    const end = matchingCountyDataEnd.find(
                      (endCounty: KeyValue) => endCounty.CNTYNAMB === county,
                    ) || { count: 0, sum_DEPSUMBR: 0 };

                    const normalRate =
                      (end.sum_DEPSUMBR - start.sum_DEPSUMBR) /
                      (start.sum_DEPSUMBR === 0 ? 1 : start.sum_DEPSUMBR);
                    const altRate = start.sum_DEPSUMBR === 0 ? 1 : normalRate;

                    return {
                      [county]: {
                        [startDate]: start,
                        [endDate]: end,
                        fips: { ...start, ...end }.STCNTYBR,
                        change: {
                          count: end.count - start.count,
                          sum_DEPSUMBR: end.sum_DEPSUMBR - start.sum_DEPSUMBR,
                          rate:
                            start.sum_DEPSUMBR + end.sum_DEPSUMBR === 0
                              ? 0
                              : altRate,
                        },
                      },
                    };
                  },
                );
                setGrowthRateTableData(tableData);
              });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setLoading(false);
          });
      })();
    }
  }, [
    appState.filters.bhcRSSD,
    appState.filters.cert,
    appState.filters.lastYear,
    appState.filters.stalpbr,
    appState.ui.institutionType,
    appStateFilters,
    endDate,
    initialLoad,
    prevInstType,
    previousState,
    startDate,
  ]);

  const downloadTableData: string[][] = [
    [
      `${
        appState.filters.bhcRSSD ? "Bank Holding Company Name" : "Institution"
      }`,
      `${appState.filters.cert ? "Cert" : "RSSDHCR"}`,
      "State",
      "County",
      "FIPS Code",
      `${startDate}: Office Count`,
      `${startDate}: ${
        appState.ui.institutionType === "bhcs"
          ? "Total Combined Deposits"
          : "Total Deposits"
      } (Thousands USD)`,
      `${endDate}: Office Count`,
      `${endDate}: ${
        appState.ui.institutionType === "bhcs"
          ? "Total Combined Deposits"
          : "Total Deposits"
      } (Thousands USD)`,
      "Change: Office Count",
      `Change: ${
        appState.ui.institutionType === "bhcs"
          ? "Total Combined Deposits"
          : "Total Deposits"
      } (Thousands USD)`,
      "Deposit Growth Rate (%)",
    ],
  ];

  const sortedGrowthRate = Object.keys(growthRateTableData)
    .map(
      (stateAbbr) =>
        selectedStatesAlpha.find((state: KeyValue) => state.value === stateAbbr)
          ?.label || stateAbbr,
    )
    .sort();
  const tableBody: React.JSX.Element[] = sortedGrowthRate.map(
    (stateName: string) => {
      const stateAbbr: string =
        selectedStatesAlpha.find((state: KeyValue) => state.label === stateName)
          ?.value || stateName;
      const counties = growthRateTableData[stateAbbr].counties;
      const stateData = growthRateTableData[stateAbbr];

      const tableRows = (data: KeyValue, name: string, countyInt?: number) => {
        downloadTableData.push([
          bankName,
          appState.filters.cert
            ? appState.filters.cert
            : appState.filters.bhcRSSD,
          countyInt ? stateName : name,
          countyInt ? name : "-",
          countyInt ? data.fips : "-",
          data[startDate]?.count?.toLocaleString(),
          data[startDate]?.sum_DEPSUMBR?.toLocaleString(),
          data[endDate]?.count?.toLocaleString(),
          data[endDate]?.sum_DEPSUMBR?.toLocaleString(),
          data["change"]?.count?.toLocaleString(),
          data["change"]?.sum_DEPSUMBR?.toLocaleString(),

          data.hasOwnProperty("change") &&
          data["change"].hasOwnProperty("rate") &&
          data["change"]["rate"]
            ? `${(data["change"]["rate"] * 100)
                .toFixed(2)
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}%`
            : "-",
        ]);
        const stateClick = !countyInt
          ? () =>
              setGrowthRateTableData({
                ...growthRateTableData,
                [stateAbbr]: {
                  ...growthRateTableData[stateAbbr],
                  expanded: !growthRateTableData[stateAbbr].expanded,
                },
              })
          : undefined;

        const countyAltColors =
          countyInt && countyInt % 2 === 0
            ? { backgroundColor: "#e0e7f0" }
            : { backgroundColor: "#f5f9fc" };
        return (
          <TableRow
            id={`${stateAbbr}_${name}`}
            key={`${stateAbbr}_${name}`}
            style={
              countyInt && countyInt % 2 === 0
                ? { backgroundColor: "#e8ecf3" }
                : {}
            }
            className={
              countyInt
                ? classes.countyRow
                : appState.filters.stalpbr && appState.filters.stalpbr !== "ALL"
                  ? classes.stateRow2
                  : classes.stateRow
            }
          >
            <TableCell
              className={classes.rowIndent}
              style={countyInt ? countyAltColors : {}}
              onClick={stateClick}
            >
              {!countyInt &&
              !(
                appState.filters.stalpbr && appState.filters.stalpbr !== "ALL"
              ) ? (
                <Chip
                  className={classes.chip}
                  icon={
                    stateData.expanded ? (
                      <ExpandMoreIcon />
                    ) : (
                      <ChevronRightIcon />
                    )
                  }
                  label={name}
                />
              ) : (
                name
              )}
            </TableCell>
            <TableCell
              onClick={stateClick}
              style={countyInt ? countyAltColors : {}}
            />
            <TableCell
              onClick={stateClick}
              style={countyInt ? countyAltColors : {}}
            >
              {countyInt ? data.fips : ""}
            </TableCell>
            <TableCell
              {...tableCellProps(
                stateAbbr,
                name,
                startDate,
                data[startDate]?.count?.toLocaleString(),
              )}
              style={
                data[startDate]?.count
                  ? {
                      color: "#1265ff",
                      textDecoration: "underline",
                    }
                  : {}
              }
            >
              {data[startDate]?.count?.toLocaleString()}
            </TableCell>
            <TableCell
              {...tableCellProps(
                stateAbbr,
                name,
                startDate,
                data[startDate]?.count?.toLocaleString(),
              )}
              style={
                data[startDate]?.count
                  ? {
                      color: "#1265ff",
                      textDecoration: "underline",
                      textAlign: "right",
                    }
                  : { textAlign: "right" }
              }
            >
              {data[startDate]?.sum_DEPSUMBR?.toLocaleString()}
            </TableCell>
            <TableCell
              {...tableCellProps(
                stateAbbr,
                name,
                endDate,
                data[endDate]?.count?.toLocaleString(),
              )}
              style={
                data[endDate]?.count
                  ? {
                      color: "#1265ff",
                      textDecoration: "underline",
                    }
                  : {}
              }
            >
              {data[endDate]?.count?.toLocaleString()}
            </TableCell>
            <TableCell
              {...tableCellProps(
                stateAbbr,
                name,
                endDate,
                data[endDate]?.count?.toLocaleString(),
              )}
              style={
                data[endDate]?.count
                  ? {
                      textAlign: "right",
                      color: "#1265ff",
                      textDecoration: "underline",
                    }
                  : { textAlign: "right" }
              }
            >
              {data[endDate]?.sum_DEPSUMBR?.toLocaleString()}
            </TableCell>
            <TableCell onClick={stateClick}>
              {data["change"]?.count?.toLocaleString()}
            </TableCell>
            <TableCell onClick={stateClick} style={{ textAlign: "right" }}>
              {data["change"]?.sum_DEPSUMBR?.toLocaleString()}
            </TableCell>
            <TableCell
              onClick={stateClick}
              style={countyInt ? countyAltColors : {}}
            >
              {data.hasOwnProperty("change") &&
              data["change"].hasOwnProperty("rate") &&
              data["change"]["rate"]
                ? `${(data["change"]["rate"] * 100)
                    .toFixed(2)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}%`
                : "-"}
            </TableCell>
          </TableRow>
        );
      };

      const staterow = tableRows(stateData, stateName);
      const countyRows = counties?.map((county: KeyValue, int: number) => {
        const countyName = Object.keys(county)[0];
        const countyData = county[countyName];
        return tableRows(countyData, countyName, int + 1);
      });

      return (
        <Fragment key={stateAbbr}>
          {staterow}
          {stateData.expanded ||
          (appState.filters.stalpbr && appState.filters.stalpbr !== "ALL") ? (
            countyRows
          ) : (
            <></>
          )}
        </Fragment>
      );
    },
  );

  downloadTableData.push([
    "National Totals",
    "",
    "",
    "",
    "",
    totals.startDate?.count?.toLocaleString(),
    totals.startDate?.sum_DEPSUMBR?.toLocaleString(),
    totals.endDate?.count?.toLocaleString(),
    totals.endDate?.sum_DEPSUMBR?.toLocaleString(),
    totals.change?.count?.toLocaleString(),
    totals.change?.sum_DEPSUMBR?.toLocaleString(),
    `${(totals?.rate * 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}%`,
  ]);
  const tableTotals: React.JSX.Element = (
    <TableRow key={totals.startDate}>
      <TableCell
        colSpan={2}
        style={headerStyle(primaryHeaderColor, "left", "5px")}
      >
        {appState.filters.stalpbr && appState.filters.stalpbr !== "ALL"
          ? "Totals"
          : "National Totals"}
      </TableCell>
      <TableCell style={headerStyle(primaryHeaderColor, "")} />
      <TableCell style={headerStyle(secondaryHeaderColor, "")}>
        {totals.startDate?.count?.toLocaleString()}
      </TableCell>
      <TableCell style={headerStyle(secondaryHeaderColor, "right")}>
        {totals.startDate?.sum_DEPSUMBR?.toLocaleString()}
      </TableCell>
      <TableCell style={headerStyle(additionalHeaderColor, "")}>
        {totals.endDate?.count?.toLocaleString()}
      </TableCell>
      <TableCell style={headerStyle(additionalHeaderColor, "right")}>
        {totals.endDate?.sum_DEPSUMBR?.toLocaleString()}
      </TableCell>
      <TableCell style={headerStyle(secondaryHeaderColor, "")}>
        {totals.change?.count?.toLocaleString()}
      </TableCell>
      <TableCell style={headerStyle(secondaryHeaderColor, "right")}>
        {totals.change?.sum_DEPSUMBR?.toLocaleString()}
      </TableCell>
      <TableCell style={headerStyle(primaryHeaderColor, "")}>
        {(totals?.rate * 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}%
      </TableCell>
    </TableRow>
  );
  const title =
    appState.filters.stalpbr && appState.filters.stalpbr !== "ALL"
      ? `Growth Rate Results - ${
          selectedStatesAlpha.find(
            (state: KeyValue) => state.value === appState.filters.stalpbr,
          )?.label || appState.filters.stalpbr
        } by Counties`
      : "Growth Rate Results - Nationally By State & County";
  const apiData: apiDownloadProps[] = [
    {
      appState,
      fileName: `${fileNameFormat(title)}.csv`,
      apiPath: "sod",
      text: "Download Results Data - formatted",
      data: downloadTableData,
    },
  ];

  const footer = (
    <Typography className={classes.footer}>
      <b>Note about this data for BHCs or Bank Holding Companies:</b>
      <br />
      This search will return results for top regulatory bank holding companies
      and their FDIC-insured subsidiaries. The regulatory top holder is assigned
      by the Federal Reserve Board based on ownership and control percentages.
      Thrift bank holding company information is not available. Official bank
      holding company information can be found by visiting the website of the
      Federal Reserve System - National Information Center.
    </Typography>
  );

  return appState.search.name || appState.search.bhcName ? (
    <DepositsNameSearchTable searchPills={searchPills} />
  ) : appState.ui.displayResults === "msas" ? (
    <>
      <Typography
        sx={{
          color: (theme) => theme.palette.primary.main,
          fontSize: 26,
          fontWeight: "bold",
          paddingBottom: "1rem",
        }}
      >
        Growth Rate Results - Metropolitan Statistical Areas (MSAs)
      </Typography>
      <GrowthRateMSATable appState={appState} searchPills={searchPills} />
      {appState.ui.institutionType === "bhcs" && (
        <Typography className={classes.footerText}>
          *The total deposits for the Bank Holding Company is the combined Total
          Domestic Deposits of the underlying Insured Subsidiaries.
        </Typography>
      )}
      <br />
      {(appState.filters.bhcRSSD || appState.ui.institutionType === "bhcs") &&
        footer}
    </>
  ) : (
    <Root>
      {branchOfficeModal}

      <Typography
        sx={{
          color: (theme) => theme.palette.primary.main,
          fontSize: 26,
          fontWeight: "bold",
          paddingBottom: "1rem",
        }}
      >
        {title}
      </Typography>
      {noResults ? (
        <Typography className={classes.subHeading}>No Results</Typography>
      ) : (
        <Box
          sx={{
            marginTop: -1,
            display: "flex",
            alignItems: "end",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              color: (theme) => theme.palette.primary.main,
              fontSize: 26,
              fontWeight: "bold",
              paddingBottom: "1rem",
            }}
          >
            {appState.filters.bhcRSSD
              ? `Combined Insured Subsidiaries of ${bankName}`
              : bankName}
          </Typography>
          <ApiDataDownload app="SOD" apiData={apiData} />
        </Box>
      )}
      <Divider />
      {searchPills}
      {moreInformation}

      <MultiHeaderTable
        useStickyHeader
        tableBody={
          loading ? (
            <LoadingIndicator text="Loading" spinnerSize={32} table />
          ) : (
            <>
              {noResults ? (
                <></>
              ) : (
                <>
                  {tableBody}
                  {tableTotals}
                </>
              )}
            </>
          )
        }
        tableHeaders={tableHeaders}
      />
      {appState.ui.institutionType === "bhcs" && (
        <Typography className={classes.footerText}>
          *The total deposits for the Bank Holding Company is the combined Total
          Domestic Deposits of the underlying Insured Subsidiaries.
        </Typography>
      )}
      <br />
      {(appState.filters.bhcRSSD || appState.ui.institutionType === "bhcs") &&
        footer}
      {noResults && (
        <Typography align="center" variant="body2" color="primary.main">
          Double-check your search criteria for errors, or try your search again
          limiting or removing location-based criteria.
        </Typography>
      )}
    </Root>
  );
}
